/**
 * Viewer-scoped effect handlers. Mounted inside the viewer (where
 * `useViewerContext` is available) so NAVIGATE_TO_DICOM and
 * HIGHLIGHT_MEASUREMENT can drive the loaded reader. A measurement is
 * "highlighted" by navigating to its DICOM and seeking to its frame — the same
 * action a measurement-label click performs.
 */
import { useViewerContext } from '@/features/study-detail/Viewer/ViewerContext';
import { useGetStudyReportsQuery } from '@/state/slices/reports.ts';
import { useCopilotEffect } from '../useCopilotEffect';

export function CopilotViewerBridges() {
  const { studyUuid, navigateToDicomByUuid, goToAnnotation } =
    useViewerContext();
  const { data: reports } = useGetStudyReportsQuery(studyUuid, {
    skip: !studyUuid,
  });

  useCopilotEffect('NAVIGATE_TO_DICOM', (p) => {
    if (p.dicom_uuid) navigateToDicomByUuid(p.dicom_uuid);
  });

  useCopilotEffect('HIGHLIGHT_MEASUREMENT', (p) => {
    const report = reports?.results?.[0];
    if (!report) return;

    // Resolve the measurement (human report first, then AI) to a DICOM + frame.
    let dicomUuid: string | undefined;
    let frame: number | undefined;

    const m = report.measurements?.find((x) => x.uuid === p.measurement_id);
    if (m?.annotation) {
      dicomUuid = m.annotation.dicomUuid;
      frame = m.annotation.frameNumber;
    } else {
      const ai = report.aiMeasurements?.find(
        (x) => x.uuid === p.measurement_id
      );
      if (ai?.annotation) {
        dicomUuid = ai.annotation.dicom;
        frame = ai.annotation.frameNumber;
      }
    }

    if (!dicomUuid) return;
    goToAnnotation(dicomUuid, p.frame_index ?? frame ?? 0);
  });

  return null;
}
